import React from 'react';
import { AlertCircle, ArrowLeft, Eye, EyeOff, Loader2 } from 'lucide-react';

/**
 * Login v2 form controls (TASK-201) — pixel-ported from
 * `design/Login v2.dc.html` (9px control radius, icon-in-field inputs,
 * 11px-pad cobalt submit). Token colors only; login-local because the shared
 * ui primitives carry the 8px control radius and no leading-icon slot.
 */

export const LoginLabel: React.FC<{ htmlFor: string; children: React.ReactNode }> = ({
  htmlFor,
  children,
}) => (
  <label htmlFor={htmlFor} className="mb-s2 block text-[11.5px] font-semibold text-ink-secondary">
    {children}
  </label>
);

export interface LoginInputRowProps extends React.InputHTMLAttributes<HTMLInputElement> {
  icon: React.ReactNode;
  trailing?: React.ReactNode;
}

/** The mock's field: 1px line-strong border, mail/lock icon, 13.5px input. */
export const LoginInputRow = React.forwardRef<HTMLInputElement, LoginInputRowProps>(
  ({ icon, trailing, className = '', disabled, ...rest }, ref) => (
    <div
      className={`flex items-center gap-[9px] rounded-[9px] border border-line-strong px-[12px] py-[10px] transition-colors focus-within:border-action focus-within:ring-2 focus-within:ring-action-border ${
        disabled ? 'bg-canvas' : 'bg-surface'
      } ${className}`}
    >
      <span className="inline-flex shrink-0 text-ink-muted [&>svg]:h-[15px] [&>svg]:w-[15px]">
        {icon}
      </span>
      <input
        ref={ref}
        disabled={disabled}
        className="w-full border-none bg-transparent text-[13.5px] text-ink outline-none placeholder:text-ink-muted disabled:cursor-not-allowed disabled:text-ink-muted"
        {...rest}
      />
      {trailing}
    </div>
  )
);
LoginInputRow.displayName = 'LoginInputRow';

export const LoginSubmit: React.FC<{
  loading?: boolean;
  /** Blocked for a reason other than work in flight (e.g. a resend cooldown). */
  disabled?: boolean;
  icon?: React.ReactNode;
  children: React.ReactNode;
}> = ({ loading, disabled, icon, children }) => (
  <button
    type="submit"
    disabled={loading || disabled}
    className="flex w-full items-center justify-center gap-[7px] rounded-[9px] bg-action p-[11px] text-[13.5px] font-semibold text-white transition-colors hover:bg-action-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-action focus-visible:ring-offset-1 disabled:pointer-events-none disabled:opacity-60"
  >
    {loading ? (
      <Loader2 className="h-[17px] w-[17px] animate-spin" />
    ) : (
      <>
        {children}
        {icon}
      </>
    )}
  </button>
);

export const LoginError: React.FC<{ message: string }> = ({ message }) => (
  <div
    role="alert"
    className="mb-s6 flex items-start gap-s3 rounded-[9px] border border-danger-border bg-danger-bg px-[12px] py-[10px]"
  >
    <AlertCircle className="mt-px h-[15px] w-[15px] shrink-0 text-danger-ink" />
    <p className="text-body-sm text-danger-ink">{message}</p>
  </div>
);

/** "Use different email" back affordance on the password steps. */
export const LoginBackButton: React.FC<{
  onClick: () => void;
  label?: string;
  /** Drop the bottom margin when the button is the last thing in the card. */
  last?: boolean;
}> = ({ onClick, label = 'Use different email', last = false }) => (
  <button
    type="button"
    onClick={onClick}
    className={`${last ? '' : 'mb-[16px] '}inline-flex items-center gap-s1 self-start text-body-sm font-medium text-ink-secondary transition-colors hover:text-ink`}
  >
    <ArrowLeft className="h-[13px] w-[13px]" />
    {label}
  </button>
);

/** A secondary action in the card that reads as a link ("Forgot password?"). */
export const LoginTextLink: React.FC<{
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}> = ({ onClick, disabled, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="rounded-[4px] text-body-sm font-semibold text-action transition-colors hover:text-action-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-action disabled:cursor-not-allowed disabled:font-medium disabled:text-ink-muted"
  >
    {children}
  </button>
);

/** Show/hide for a password field, in the field's trailing slot. */
export const LoginEyeToggle: React.FC<{ shown: boolean; onToggle: () => void }> = ({ shown, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    aria-label={shown ? 'Hide password' : 'Show password'}
    className="inline-flex shrink-0 text-ink-muted transition-colors hover:text-ink-secondary [&>svg]:h-[15px] [&>svg]:w-[15px]"
  >
    {shown ? <EyeOff /> : <Eye />}
  </button>
);
